import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Phone, Bike, Navigation, CheckCircle2, Loader2, UserCheck, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DriverSettingsPanel, shareDriverViaWhatsApp, openDriverPdf } from '../../components/chauffeur/DriverSettings';
import { LiveMap } from '../../components/chauffeur/LiveMap';
import { PickupAddressDialog } from '../../components/chauffeur/PickupAddressDialog';
import { AllOrdersTab } from '../../components/chauffeur/AllOrdersTab';
import { RouteOrderDialog } from '../../components/chauffeur/RouteOrderDialog';
import { FirstStopSelect } from '../../components/chauffeur/FirstStopSelect';
import { todayStr, addDays, dayLabel } from '../../components/chauffeur/routeDate';
import { PlanDayTabs } from '../../components/chauffeur/PlanDayTabs';
import { MoveDayButton } from '../../components/chauffeur/MoveDayButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRIVER_COLORS = {
  Lippe:      { bg: 'bg-rose-500',    light: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-300' },
  Sebastiano: { bg: 'bg-blue-500',    light: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-300' },
  Daniel:     { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300' },
  Sandro:     { bg: 'bg-amber-500',   light: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-300' },
};

const RouteDatePicker = ({ value, onChange }) => {
  const t = todayStr();
  const quick = [[t, 'Vandaag'], [addDays(t, 1), 'Morgen']];
  return (
    <div className="flex items-center gap-1 border border-zinc-300 rounded-lg p-1" data-testid="route-date-picker">
      {quick.map(([d, label]) => (
        <button key={d} onClick={() => onChange(d)} data-testid={`route-date-${label.toLowerCase()}`}
                className={`px-2.5 py-1.5 rounded text-sm font-medium ${value === d ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-100'}`}>
          {label}
        </button>
      ))}
      <input type="date" value={value} min={t} onChange={(e) => e.target.value && onChange(e.target.value)}
             className="text-sm px-1.5 py-1 rounded border-0 bg-transparent" data-testid="route-date-input" />
    </div>
  );
};

export default function AdminChauffeurRoute() {
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [driver, setDriver] = useState('Lippe');
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState('all'); // all | foreign | nl
  const [days, setDays] = useState(() => Number(localStorage.getItem('chauffeur_period_days') || 0));
  const [editTarget, setEditTarget] = useState(null);
  const [tab, setTab] = useState('route');
  const [orderDriver, setOrderDriver] = useState(null);
  const [routeDate, setRouteDate] = useState(todayStr());

  const load = async (scopeOverride, daysOverride) => {
    setLoading(true);
    try {
      const s = scopeOverride ?? scope;
      const d = daysOverride ?? days;
      const res = await axios.get(`${API}/admin/chauffeur-open-pickups?scope=${s}${d ? `&days=${d}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ophalen mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const allMotors = useMemo(() => {
    if (!data) return [];
    return data.stops.flatMap((s) => s.motors.map((m) => ({ ...m, stop_id: s.stop_id })));
  }, [data]);

  const toggleMotor = (orderId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleDealer = (stop) => {
    const ids = stop.motors.map((m) => m.order_id);
    const allSelected = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(allMotors.map((m) => m.order_id)));
  };

  const clearSelection = () => setSelected(new Set());

  const assign = async () => {
    if (selected.size === 0) {
      toast.error('Selecteer eerst motoren');
      return;
    }
    setBusy(true);
    try {
      toast.loading('Route optimaliseren…', { id: 'optimizing' });
      const res = await axios.post(
        `${API}/admin/chauffeur-assign`,
        { order_ids: Array.from(selected), driver, route_date: routeDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss('optimizing');
      const km = res.data.estimated_total_km;
      const stops = res.data.stop_count;
      toast.success(
        `${res.data.message} · ${stops} stops · ~${km} km (geoptimaliseerd)`,
        { duration: 15000, action: { label: '📄 PDF printen', onClick: () => openPdf(driver, routeDate) } }
      );
      if (res.data.missing_coords?.length > 0) {
        toast.warning(
          `${res.data.missing_coords.length} leverancier(s) zonder adres: ${res.data.missing_coords.join(', ')}`,
          { duration: 7000 }
        );
      }
      if (res.data.google_maps_url) {
        window.open(res.data.google_maps_url, '_blank', 'noopener,noreferrer');
      }
      await load();
    } catch (e) {
      toast.dismiss('optimizing');
      toast.error(e?.response?.data?.detail || 'Toewijzen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (orderIds) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/chauffeur-unassign`,
        { order_ids: orderIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Toewijzing verwijderd');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mislukt');
    } finally {
      setBusy(false);
    }
  };

  const markPickedUp = async (orderIds) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/chauffeur-mark-picked-up`,
        { order_ids: orderIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${orderIds.length} motor(en) gemarkeerd als opgehaald`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mislukt');
    } finally {
      setBusy(false);
    }
  };

  const openPdf = (drv, date) => openDriverPdf(drv, token, date);

  // Groepeer per driver voor overview (reeds toegewezen) — MOET boven early return
  const assignedByDriver = useMemo(() => {
    if (!data) return {};
    const grouped = {};
    data.stops.forEach((s) => {
      s.motors.forEach((m) => {
        if (m.assigned_driver) {
          const k = `${m.route_date}|${m.assigned_driver}`;
          if (!grouped[k]) grouped[k] = [];
          grouped[k].push({ ...m, stop: s });
        }
      });
    });
    return Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)));
  }, [data]);

  const plannedByDate = useMemo(() => {
    const out = {};
    Object.entries(assignedByDriver).forEach(([k, ms]) => { const d = k.split('|')[0]; out[d] = (out[d] || 0) + ms.length; });
    return out;
  }, [assignedByDriver]);
  const dayCards = Object.entries(assignedByDriver).filter(([k]) => k.split('|')[0] === routeDate);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  const nothing = !data || data.total_motors === 0;
  const selectedCount = selected.size;
  const maxPerRoute = data?.max_motors_per_route || 13;
  const overLimit = selectedCount > maxPerRoute;

  return (
    <div className="min-h-screen bg-zinc-50 pb-32">
      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <Link to="/admin/orders" className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900" data-testid="chauffeur-back">
            <ArrowLeft className="w-4 h-4" /> Terug naar bestellingen
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/admin/ritoverzicht" className="text-sm font-medium text-purple-600 hover:text-purple-800" data-testid="chauffeur-trip-summary-link">
              📊 Ritoverzicht
            </Link>
            <button onClick={() => load()} className="text-sm text-purple-600 hover:text-purple-800" data-testid="chauffeur-refresh">
              🔄 Vernieuwen
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="chauffeur-title">
                <Navigation className="w-6 h-6" /> Chauffeur Route
              </h1>
              <p className="text-sm text-purple-100 mt-1">
                Vink motoren aan → kies chauffeur → "Toewijzen" opent Google Maps automatisch
              </p>
              <p className="text-xs text-purple-200 mt-0.5">
                Start: <b>{data?.hq_address}</b>
              </p>
              <p className="text-xs text-purple-200 mt-0.5">
                Eind: <b>{data?.end_address || 'Coppet, Switzerland'}</b> · Max {maxPerRoute} motoren per route
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold" data-testid="chauffeur-total-motors">{data?.total_motors ?? 0}</div>
              <div className="text-xs text-purple-100 uppercase tracking-wider">Motoren openstaand</div>
              <div className="text-sm mt-1">{data?.total_stops ?? 0} leveranciers</div>
            </div>
          </div>
        </div>

        <div className="flex gap-1 bg-white rounded-xl border border-zinc-200 p-1 w-fit" data-testid="chauffeur-main-tabs">
          {[['route', '🗺️ Route plannen'], ['all', '✅ Ophaallijst']].map(([v, label]) => (
            <button key={v} onClick={() => { setTab(v); if (v === 'route') load(); }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === v ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}
                    data-testid={`chauffeur-tab-${v}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'all' ? <AllOrdersTab onChanged={() => {}} /> : (<>
        {/* Scope-filter tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white rounded-xl border border-zinc-200 p-1">
            {[
              { v: 'all', label: '🌍 Alle', desc: 'NL + Buitenland' },
              { v: 'foreign', label: '🇪🇺 Buitenland', desc: 'Alleen buitenlandse leveranciers' },
              { v: 'nl', label: '🇳🇱 Nederland', desc: 'Alleen NL leveranciers' },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => { setScope(opt.v); load(opt.v); }}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                  scope === opt.v
                    ? 'bg-purple-600 text-white'
                    : 'text-zinc-700 hover:bg-zinc-100'
                }`}
                title={opt.desc}
                data-testid={`scope-${opt.v}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <select
            value={days}
            onChange={(e) => { const d = Number(e.target.value); setDays(d); localStorage.setItem('chauffeur_period_days', String(d)); load(undefined, d); }}
            className={`text-sm px-3 py-2 rounded-lg border font-medium ${days ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-zinc-200 text-zinc-700'}`}
            title="Periode: alle niet-opgehaalde pakbonnen van die periode, ook onbevestigde bestellingen"
            data-testid="period-select"
          >
            <option value={0}>📅 Standaard</option>
            <option value={21}>📅 Laatste 3 weken</option>
            <option value={92}>📅 Laatste 3 maanden</option>
            <option value={183}>📅 Laatste 6 maanden</option>
          </select>
        </div>

        <PlanDayTabs value={routeDate} onChange={setRouteDate} plannedByDate={plannedByDate} />

        <LiveMap token={token} />
        <RouteOrderDialog driver={orderDriver?.driver} date={orderDriver?.date} onClose={() => setOrderDriver(null)} />
        <PickupAddressDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={() => load()} />

        <DriverSettingsPanel token={token} />

        {/* Reeds toegewezen overview */}
        {(
          <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="assigned-section">
            <h2 className="text-sm font-semibold text-zinc-900 mb-2 flex items-center gap-1">
              <UserCheck className="w-4 h-4" /> Ingepland voor {dayLabel(routeDate).toLowerCase()}
              <span className="font-normal text-zinc-500">({new Date(`${routeDate}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })})</span>
            </h2>
            {dayCards.length === 0 && (
              <p className="text-sm text-zinc-500" data-testid="assigned-empty">Nog niets ingepland. Vink hieronder motoren aan en kies een chauffeur.</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {dayCards.map(([key, motors]) => {
                const [date, drv] = key.split('|');
                const c = DRIVER_COLORS[drv] || DRIVER_COLORS.Lippe;
                const id = `${drv}-${date}`;
                return (
                  <div key={key} className={`${c.light} ${c.border} border rounded-lg p-3`} data-testid={`assigned-card-${id}`}>
                    <div className="flex items-center justify-between gap-1">
                      <span className={`${c.text} text-xs font-semibold uppercase`}>{drv}</span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white border border-zinc-300 text-zinc-700" data-testid={`assigned-date-${id}`}>
                        {dayLabel(date)} · {new Date(`${date}T12:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'numeric' })}
                      </span>
                    </div>
                    <div className="text-2xl font-bold text-zinc-900">{motors.length}</div>
                    <div className="text-xs text-zinc-600">motoren</div>
                    <FirstStopSelect driver={drv} date={date} refreshKey={`${motors.length}-${orderDriver?.driver}`} />
                    <button
                      onClick={() => openPdf(drv, date)}
                      className="w-full mt-2 text-xs px-2 py-1.5 bg-white hover:bg-purple-100 border border-purple-300 text-purple-800 rounded font-semibold"
                      data-testid={`driver-pdf-${id}`}
                    >
                      📄 Route-PDF printen
                    </button>
                    <button
                      onClick={() => setOrderDriver({ driver: drv, date })}
                      className="w-full mt-1 text-xs px-2 py-1.5 bg-white hover:bg-purple-100 border border-purple-300 text-purple-800 rounded font-semibold"
                      data-testid={`driver-reorder-${id}`}
                    >
                      ✏️ Volgorde aanpassen
                    </button>
                    <MoveDayButton driver={drv} date={date} token={token} onMoved={(to) => { setRouteDate(to); load(); }} />
                    <button
                      onClick={() => shareDriverViaWhatsApp(drv, token, date)}
                      className="w-full mt-1 text-xs px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded font-semibold"
                      data-testid={`driver-whatsapp-${id}`}
                    >
                      💬 Stuur via WhatsApp
                    </button>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => markPickedUp(motors.map((m) => m.order_id))}
                        disabled={busy}
                        className="flex-1 text-[10px] px-1.5 py-1 bg-white hover:bg-emerald-100 border border-emerald-300 text-emerald-800 rounded font-medium disabled:opacity-50"
                        data-testid={`driver-done-${id}`}
                      >
                        ✅ Opgehaald
                      </button>
                      <button
                        onClick={() => unassign(motors.map((m) => m.order_id))}
                        disabled={busy}
                        className="text-[10px] px-1.5 py-1 bg-white hover:bg-red-100 border border-red-200 text-red-700 rounded disabled:opacity-50"
                        data-testid={`driver-unassign-${id}`}
                        title="Toewijzing verwijderen"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {nothing && (
          <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-zinc-900">Alles opgehaald</h2>
            <p className="text-sm text-zinc-600 mt-1">Er zijn geen openstaande pakbonnen.</p>
          </div>
        )}

        {!nothing && (
          <>
            {/* Selectie-knoppen */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={selectAll}
                className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
                data-testid="select-all"
              >
                Alles selecteren ({data?.total_motors})
              </button>
              <button
                onClick={clearSelection}
                className="text-xs px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
                data-testid="clear-selection"
              >
                Selectie wissen
              </button>
              <span className="text-xs text-zinc-500 ml-auto">
                Tip: Klik op een leverancier om al zijn motoren aan/uit te vinken
              </span>
            </div>

            {/* Dealer stops met checkboxes */}
            {data?.stops?.map((stop) => {
              const dealerAllIds = stop.motors.map((m) => m.order_id);
              const allSelected = dealerAllIds.every((id) => selected.has(id));
              const someSelected = dealerAllIds.some((id) => selected.has(id));
              return (
                <div
                  key={stop.stop_id}
                  className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm"
                  data-testid={`supplier-stop-${stop.stop_id}`}
                >
                  <div
                    className={`p-4 cursor-pointer hover:bg-zinc-50 ${someSelected ? 'bg-purple-50' : ''}`}
                    onClick={() => toggleDealer(stop)}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleDealer(stop)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-5 h-5 rounded accent-purple-600 cursor-pointer"
                          data-testid={`supplier-check-${stop.stop_id}`}
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-zinc-900 flex items-center gap-2">
                            {stop.company}
                            {!(stop.source === 'info' && stop.company === 'Leverancier onbekend') && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditTarget({ orderIds: stop.motors.map((m) => m.order_id), userId: stop.user_id, edit: stop.edit, title: `Ophaaladres ${stop.company}` }); }}
                                className="text-xs font-medium px-2 py-0.5 rounded-md border border-purple-300 text-purple-700 hover:bg-purple-100"
                                data-testid={`edit-address-${stop.stop_id}`}
                              >
                                ✏️ {stop.address ? 'Wijzig' : 'Adres invullen'}
                              </button>
                            )}
                            {stop.country && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-zinc-200 text-zinc-700 rounded uppercase tracking-wide font-mono">
                                {stop.country}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-zinc-600 flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                            {stop.address || <em className="text-red-600">Adres ontbreekt{stop.company === 'Leverancier onbekend' ? ' — vul per motor de leverancier in (✏️)' : ' — klik op ✏️ Adres invullen'}</em>}
                          </div>
                          {stop.phone && (
                            <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" />
                              <a href={`tel:${stop.phone}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                                {stop.phone}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {stop.waiting_days != null && (
                          <span
                            className={`text-xs font-semibold px-2 py-1 rounded-md ${stop.waiting_days > 30 ? 'bg-red-100 text-red-700' : stop.waiting_days > 14 ? 'bg-amber-100 text-amber-800' : 'bg-zinc-100 text-zinc-600'}`}
                            data-testid={`stop-waiting-${stop.stop_id}`}
                          >
                            oudste: {stop.waiting_days} d
                          </span>
                        )}
                        <div className="flex items-center gap-1 text-purple-700 font-bold text-lg">
                          <Bike className="w-5 h-5" />
                          {stop.motors.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Motor list */}
                  <div className="divide-y divide-zinc-100 bg-zinc-50/50">
                    {stop.motors.map((m) => {
                      const c = m.assigned_driver ? DRIVER_COLORS[m.assigned_driver] : null;
                      return (
                        <label
                          key={m.order_id}
                          className={`flex items-center gap-3 px-4 py-2 pl-12 hover:bg-white cursor-pointer ${
                            selected.has(m.order_id) ? 'bg-purple-50' : ''
                          }`}
                          data-testid={`motor-row-${m.order_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(m.order_id)}
                            onChange={() => toggleMotor(m.order_id)}
                            className="w-4 h-4 rounded accent-purple-600"
                            data-testid={`motor-check-${m.order_id}`}
                          />
                          <Bike className="w-4 h-4 text-zinc-400" />
                          <span className="text-sm text-zinc-800 flex-1">
                            {(m.make || m.model) ? `${m.make} ${m.model}` : <em className="text-zinc-500">Motor zonder naam</em>}
                            {m.license_plate && <span className="text-zinc-500 ml-2">({m.license_plate})</span>}
                            {m.status && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-zinc-200 text-zinc-700 rounded uppercase tracking-wide">
                                {m.status}
                              </span>
                            )}
                            {m.manual && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold" data-testid={`manual-badge-${m.order_id}`}>📌 Handmatig</span>}
                            {m.buyer && <span className="ml-2 text-[10px] text-zinc-500">→ {m.buyer}</span>}
                            {m.date && (
                              <span className="ml-2 text-[10px] text-zinc-500" data-testid={`motor-date-${m.order_id}`}>
                                {new Date(m.date).toLocaleDateString('nl-NL')}
                                {' · '}wacht {Math.max(0, Math.floor((Date.now() - new Date(m.date).getTime()) / 86400000))} d
                              </span>
                            )}
                          </span>
                          {m.vin && (
                            <span className="text-[10px] font-mono text-zinc-400 hidden sm:inline">{m.vin}</span>
                          )}
                          {m.assigned_driver && c && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.bg} text-white font-semibold`}>
                              {m.assigned_driver}{m.route_date && m.route_date > todayStr() ? ` · ${dayLabel(m.route_date)}` : ''}
                            </span>
                          )}
                          {stop.company === 'Leverancier onbekend' && (
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditTarget({ orderIds: [m.order_id], userId: null, edit: {}, title: `Leverancier voor ${m.make} ${m.model}`.trim() }); }}
                              className="text-[11px] font-medium px-2 py-0.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`edit-motor-supplier-${m.order_id}`}
                            >
                              ✏️ Leverancier
                            </button>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
        </>)}
      </div>

      {/* Sticky action bar onderaan */}
      {selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 shadow-2xl z-40">
          <div className="max-w-5xl mx-auto p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`text-2xl font-bold ${overLimit ? 'text-red-600' : 'text-purple-700'}`}>
                {selectedCount}
              </div>
              <div className="text-sm">
                <div className="text-zinc-900 font-medium">motoren geselecteerd</div>
                {overLimit && (
                  <div className="text-xs text-red-600">⚠️ Over max van {maxPerRoute}</div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 border border-zinc-300 rounded-lg p-1">
                {['Lippe', 'Sebastiano', 'Daniel', 'Sandro'].map((d) => {
                  const c = DRIVER_COLORS[d];
                  const active = driver === d;
                  return (
                    <button
                      key={d}
                      onClick={() => setDriver(d)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                        active ? `${c.bg} text-white` : 'text-zinc-700 hover:bg-zinc-100'
                      }`}
                      data-testid={`driver-select-${d}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>

              <RouteDatePicker value={routeDate} onChange={setRouteDate} />

              <button
                onClick={clearSelection}
                className="px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-sm"
                data-testid="cancel-selection"
              >
                Annuleer
              </button>

              <button
                onClick={assign}
                disabled={busy}
                className={`px-4 py-2 rounded-lg text-white font-semibold flex items-center gap-2 disabled:opacity-50 ${DRIVER_COLORS[driver].bg} hover:opacity-90`}
                data-testid="assign-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                Toewijzen aan {driver} ({dayLabel(routeDate)}) + open route
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
